import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import useAxios from 'axios-hooks'
import axios from 'axios'
import moment from 'moment'
import { isEmpty } from 'lodash'
import { useNavigate, useParams } from 'react-router'
import { AppContext } from '../../context/app'
import { IPTV_CHANNEL_FILTER, IPTV_CHANNEL_PLAYLIST, SYSTEM_METADATA } from '../../utils/apiUrls'
import CustomImage from '../../components/customImage/customImage'
import { getImage } from '../../utils/helper'
import { LG_SIZE, XL_SIZE } from '../../utils/constants'
import { useInterval } from '../../hooks/useInterval'
import ProgramPreview from './programPreview'

const IptvPlaylistEPG = ({ currentPlayingContent, removeCategories = false }) => {
  const navigate = useNavigate()
  const { accountObj } = useContext(AppContext)
  const channelId = useParams().channelId

  const [allChannelList, setAllChannelList] = useState([])
  const [allCategories, setAllCategories] = useState([])
  const [selectedCategory, setSelectedCategory] = useState({
    name: 'all',
    id: 'all',
  })
  const [channelPlaylistMap, setChannelPlaylistMap] = useState({})
  const [channelSliderHeight, setChannelSliderHeight] = useState(0)
  const [timeConfig, setTimeConfig] = useState('sm')
  const [timerLeftMargin, setTimerLeftMargin] = useState(0)
  const [epgLoader, setEpgLoader] = useState(false)
  const [selectedPreviewProgram, setSelectedPreviewProgram] = useState({})
  const [hoveredContent, setHoveredContent] = useState('')

  const channelRef = useRef(null)
  const epgRef = useRef(null)
  const timeSlotRef = useRef(null)
  const channelSliderHeightRef = useRef(null)
  const channelPlaylistSignatureRef = useRef('')
  const playlistRequestInFlightRef = useRef(false)

  const timeStyleConfig = {
    xxl: {
      width: '259px',
      fontSize: '14px',
      timeFont: '13px',
      pixelPerMinute: 8.6333,
    },
    xl: { width: '207px', fontSize: '11px', timeFont: '10px', pixelPerMinute: 6.9 },
    sm: { width: '86px', fontSize: '10px', timeFont: '9px', pixelPerMinute: 2.8666 },
  }

  const [{ data: categoryRes, loading: categoryLoading }, categoryCall] = useAxios(
    {
      url: `${SYSTEM_METADATA}`,
      method: 'get',
    },
    { manual: true },
  )

  const [{ data: channelData, loading: channelLoading }, channelCall] = useAxios(
    {
      url: IPTV_CHANNEL_FILTER,
      method: 'post',
    },
    {
      manual: true,
    },
  )

  const handleResize = () => {
    if (window.innerWidth > XL_SIZE) {
      setTimeConfig('xxl')
    } else if (window.innerWidth < XL_SIZE && window.innerWidth > LG_SIZE) {
      setTimeConfig('xl')
    } else {
      setTimeConfig('sm')
    }
  }

  useEffect(() => {
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    categoryCall()
  }, [])

  useEffect(() => {
    setSelectedCategory({
      name: 'all',
      id: 'all',
    })
    setAllChannelList([])
    setChannelPlaylistMap({})
    channelPlaylistSignatureRef.current = ''

    channelCall({
      data: {
        filters: {},
        order_by: 'name',
        reverse: true,
        page_size: '1000',
        page: '1',
      },
    })
  }, [channelId])

  useEffect(() => {
    if (channelData && channelData.success) {
      const sortedChannels = [...channelData.data].sort((a, b) =>
        a._id === channelId ? -1 : b._id === channelId ? 1 : 0,
      )
      setAllChannelList(sortedChannels)
    }
  }, [channelData, channelId])

  useEffect(() => {
    if (categoryRes && categoryRes.success) {
      setAllCategories(categoryRes.data.filter((item) => item.type === 'category') || [])
    }
  }, [categoryRes])

  const getRoundedStartTime = () => {
    const now = new Date()

    if (now.getMinutes() < 59) {
      if (now.getMinutes() < 30) {
        now.setMinutes(0, 0, 0)
      } else {
        now.setMinutes(30, 0, 0)
      }
    } else {
      now.setHours(now.getHours() + 1, 0, 0, 0)
    }

    return now
  }

  const timeWindowStart = useMemo(() => getRoundedStartTime(), [])
  const timeWindowMinutes = 48 * 60

  const normalizePlaylistData = (playlist = []) => {
    return (playlist || [])
      .map((program) => {
        const startMoment = moment.utc(program?.start_time).local()
        let endMoment = moment.utc(program?.end_time).local()

        if (!startMoment.isValid() || !endMoment.isValid()) {
          return null
        }

        if (endMoment.isSameOrBefore(startMoment)) {
          endMoment = endMoment.add(1, 'day')
        }

        return {
          ...program,
          title: program?.title || program?.name || program?.file_name || 'Untitled Program',
          programKey: `${program?.asset_id || program?.name || program?.file_name}-${
            program?.start_time
          }`,
          startMoment,
          endMoment,
        }
      })
      .filter(Boolean)
      .sort((first, second) => first.startMoment.valueOf() - second.startMoment.valueOf())
  }

  const getPlaylistMapSignature = (playlistMap = {}, channels = []) => {
    return channels
      .map((channel) => {
        const channelPrograms = playlistMap[channel._id] || []
        const programSignature = channelPrograms
          .map(
            (program) =>
              `${program.programKey}-${
                program.title
              }-${program.startMoment.valueOf()}-${program.endMoment.valueOf()}-${Boolean(
                program?.has_exact_time,
              )}-${Boolean(program?.is_looped)}`,
          )
          .join('|')

        return `${channel._id}:${programSignature}`
      })
      .join('||')
  }

  const fetchPlaylistForChannels = useCallback(async () => {
    if (isEmpty(allChannelList) || playlistRequestInFlightRef.current) {
      return
    }

    playlistRequestInFlightRef.current = true

    try {
      // Extend fetch window 24h back so programs that started before
      // timeWindowStart but are currently playing are included in the response.
      // The EPG grid rendering clips their left edge to 0 automatically.
      const windowStartUtc = moment(timeWindowStart).subtract(24, 'hours').utc().toISOString()
      const windowEndUtc = moment(timeWindowStart)
        .add(timeWindowMinutes, 'minutes')
        .utc()
        .toISOString()

      const playlistResponses = await Promise.allSettled(
        allChannelList.map((channel) =>
          axios.get(`${IPTV_CHANNEL_PLAYLIST}/${channel._id}`, {
            params: {
              start_time: windowStartUtc,
              end_time: windowEndUtc,
            },
          }),
        ),
      )

      const playlistMap = {}

      allChannelList.forEach((channel, channelIndex) => {
        const response = playlistResponses[channelIndex]
        const mergedPlaylist =
          response?.status === 'fulfilled' &&
          response.value?.data?.success &&
          !isEmpty(response.value?.data?.data)
            ? normalizePlaylistData(response.value?.data?.data?.playlist_data || [])
            : []

        playlistMap[channel._id] = mergedPlaylist
          .sort((first, second) => first.startMoment.valueOf() - second.startMoment.valueOf())
          .filter(
            (program, index, programs) =>
              programs.findIndex((item) => item.programKey === program.programKey) === index,
          )
      })

      const nextSignature = getPlaylistMapSignature(playlistMap, allChannelList)
      if (channelPlaylistSignatureRef.current === nextSignature) {
        return
      }

      channelPlaylistSignatureRef.current = nextSignature
      setChannelPlaylistMap(playlistMap)
    } finally {
      playlistRequestInFlightRef.current = false
    }
  }, [allChannelList, timeWindowMinutes, timeWindowStart])

  useEffect(() => {
    fetchPlaylistForChannels()
  }, [fetchPlaylistForChannels])

  useInterval(() => {
    fetchPlaylistForChannels()
  }, 20000)

  useEffect(() => {
    setEpgLoader(channelLoading || categoryLoading)
  }, [channelLoading, categoryLoading])

  const generateSlots = () => {
    const slots = []
    const startTime = new Date(timeWindowStart)

    for (let i = 0; i < 96; i++) {
      const slotTime = new Date(startTime.getTime() + i * 30 * 60000)

      const timeString = slotTime.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h12',
      })

      const dateString = slotTime.toLocaleDateString([], {
        day: 'numeric',
        month: 'short',
        weekday: 'short',
      })

      slots.push({ time: timeString, date: dateString })
    }

    return slots
  }

  const syncScroll = (source, target, isLeftToRight = false) => {
    if (source.current && target.current) {
      if (isLeftToRight) {
        target.current.scrollLeft = source.current.scrollLeft
        return
      }
      target.current.scrollTop = source.current.scrollTop
    }
  }

  const visibleChannels = allChannelList.filter(
    (channel) =>
      selectedCategory.id === 'all' ||
      [...channel.category].find((cat) => cat._id === selectedCategory.id),
  )

  useEffect(() => {
    if (channelSliderHeightRef.current) {
      setChannelSliderHeight(channelSliderHeightRef.current.offsetHeight)
    }
  }, [visibleChannels, channelPlaylistMap])

  const getContentTileColor = (program, programIndex) => {
    const now = moment()
    const isCurrentPlayingContent =
      currentPlayingContent?.asset_id === program?.asset_id &&
      now.isSameOrAfter(program.startMoment) &&
      now.isSameOrBefore(program.endMoment)

    if (isCurrentPlayingContent) {
      return {
        bgColor: '#141446',
        borderColor: '#1F51FF',
      }
    }

    if (hoveredContent === `${program.asset_id || program.name}-${programIndex}`) {
      return {
        bgColor: '#313740',
        borderColor: '#ffffff',
      }
    }

    return {
      bgColor: '#11171F',
      borderColor: '#727272',
    }
  }

  useInterval(
    () => {
      const currentTime = new Date()
      const minutesFromNowToStart = Math.floor(
        (currentTime.getTime() - timeWindowStart.getTime()) / 60000,
      )
      setTimerLeftMargin(minutesFromNowToStart * timeStyleConfig[timeConfig].pixelPerMinute)
    },
    timerLeftMargin === 0 ? 1000 : 30000,
  )

  const handleMouseEnter = (id) => {
    setHoveredContent(id)
  }
  const handleMouseLeave = () => {
    setHoveredContent('')
  }

  return (
    <div
      className={`w-full h-[260px] xl:h-[295px] 2xl:h-[371px] flex ${
        epgLoader ? 'animate-pulse' : ''
      }`}
    >
      {!removeCategories ? (
        <div className="mr-[3px] hidden xl:block xl:w-[99px] 2xl:w-[125px]">
          <div
            style={{ background: '#171D24', color: '#CECECE' }}
            className="w-full h-[31px] xl:h-[33px] 2xl:h-[42px] flex items-center justify-start px-2.5 2xl:px-3 text-[11px] 2xl:text-xs mb-[6px] xl:mb-[9px] 2xl:mb-[11px]"
          >
            Categories
          </div>

          <div className="tv-epg-scrollbar-class h-[226px] xl:h-[255px] 2xl:h-[320px]">
            <div
              style={{
                background: selectedCategory.id === 'all' ? '#263241' : '#171D24',
                border: selectedCategory.id === 'all' ? '1px solid #707070' : 'none',
                color: '#FFFFFF',
              }}
              className="w-full xl:h-[40px] 2xl:h-[50px] flex items-center justify-start text-xs 2xl:text-sm mb-1 cursor-pointer rounded xl:rounded-md px-2.5 2xl:px-3"
              onClick={() =>
                setSelectedCategory({
                  id: 'all',
                  name: 'all',
                })
              }
            >
              All Channels
            </div>

            {!isEmpty(allCategories) &&
              allCategories.map((item, index) => (
                <div
                  style={{
                    background: selectedCategory.id === item._id ? '#263241' : '#171D24',
                    border: selectedCategory.id === item._id ? '1px solid #707070' : 'none',
                    color: '#FFFFFF',
                    wordBreak: 'break-word',
                  }}
                  className="w-full xl:h-[40px] 2xl:h-[50px] flex items-center justify-start text-xs 2xl:text-sm mb-1 cursor-pointer rounded xl:rounded-md px-2.5 2xl:px-3"
                  key={index}
                  onClick={() =>
                    setSelectedCategory({
                      id: item._id,
                      name: item.name,
                    })
                  }
                >
                  {item.name}
                </div>
              ))}
          </div>
        </div>
      ) : null}

      <div>
        <div className="w-[78.5px] xl:w-[124px] 2xl:w-[155px]">
          <div
            style={{ background: '#12171D', color: '#CECECE' }}
            className="w-full h-[31px] xl:h-[33px] 2xl:h-[42px] flex items-center justify-center text-[11px] 2xl:text-xs mb-[6px] xl:mb-[9px] 2xl:mb-[11px] capitalize"
          >
            {selectedCategory.name} Channels ({visibleChannels.length})
          </div>

          <div
            ref={channelRef}
            onScroll={() => syncScroll(channelRef, epgRef)}
            className="hidden-scrollbar-class h-[226px] xl:h-[255px] 2xl:h-[320px]"
          >
            <div ref={channelSliderHeightRef}>
              {!isEmpty(visibleChannels)
                ? visibleChannels.map((item, index) => (
                    <div
                      style={{
                        background: channelId === item._id ? '#263241' : '#12171D',
                        color: '#FFFFFF',
                        borderRadius: '6px 0px 0px 6px',
                        border: channelId === item._id ? '1px solid #707070' : 'none',
                      }}
                      className="w-full h-[36px] xl:h-[40px] 2xl:h-[50px] flex items-center justify-center mb-[2px] xl:mb-[3px] 2xl:mb-1 cursor-pointer relative"
                      key={index}
                      onClick={() => navigate(`/channel/${item._id}`)}
                    >
                      <div className="flex" style={{ height: '75%', aspectRatio: '1.785' }}>
                        <CustomImage
                          imageUrl={getImage(
                            accountObj?.cf_domain_name,
                            item?.logo?.website?.s3_key,
                          )}
                          imgClass={'h-full w-full'}
                          loaderClass={'h-full w-full'}
                          imgAlt={item.name}
                        />
                      </div>
                    </div>
                  ))
                : [1, 2, 3, 4, 5, 6, 7].map((_, index) => (
                    <div
                      style={{
                        background: '#12171D',
                        borderRadius: '6px 0px 0px 6px',
                      }}
                      className="w-full h-[36px] xl:h-[40px] 2xl:h-[50px] mb-[2px] xl:mb-[3px] 2xl:mb-1 relative animate-pulse"
                      key={index}
                    >
                      <div className="flex" style={{ height: '75%', aspectRatio: '1.785' }}>
                        <div className="bg-gray-500 h-full w-full"></div>
                      </div>
                    </div>
                  ))}
            </div>
          </div>
        </div>
      </div>

      <div className="ml-1 relative overflow-hidden">
        <div
          style={{ overflowX: 'auto', overflowY: 'visible' }}
          className={` 
            ${removeCategories ? '' : 'w-[calc(100vw-79px)] 2xl:w-[1342px] xl:w-[1068px]'}
             tv-epg-scrollbar-horizontal flex`}
          ref={timeSlotRef}
          onScroll={() => syncScroll(timeSlotRef, epgRef, true)}
        >
          <div
            style={{
              background: '#12171D',
              color: '#CECECE',
            }}
            className="h-[31px] xl:h-[33px] 2xl:h-[42px] text-[11px] 2xl:text-xs relative"
          >
            <div style={{ height: '49.5%' }} className="flex items-center"></div>
            <div style={{ height: '49.5%', background: '#000000' }} className="flex">
              {generateSlots().map((slot, index) => (
                <div
                  key={index}
                  style={{
                    width: timeStyleConfig[timeConfig].width,
                    fontSize: timeStyleConfig[timeConfig].timeFont,
                    borderRight: `1px solid ${
                      slot.time.includes('11:30 pm') ? '#FFBD29' : '#6F6F6F'
                    } `,
                  }}
                  className="h-full pl-2 flex items-center font-semibold"
                >
                  <div
                    style={{ fontSize: timeStyleConfig[timeConfig].timeFont, color: '#FFBD29' }}
                    className="font-semibold absolute top-0"
                  >
                    {!index ? (
                      <>
                        {slot.date} <span className="font-normal">(Today)</span>
                      </>
                    ) : null}
                    {slot.time.includes('12:00 am') ? <>{slot.date}</> : null}
                  </div>
                  <div className={index ? 'uppercase' : ''}>{index ? slot.time : 'Now'}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div
          style={{ overflowX: 'auto', overflowY: 'auto' }}
          ref={epgRef}
          onScroll={() => {
            syncScroll(epgRef, channelRef)
            syncScroll(epgRef, timeSlotRef, true)
          }}
          className={` 
            ${removeCategories ? '' : 'w-[calc(100vw-79px)] 2xl:w-[1342px] xl:w-[1068px]'}
             tv-epg-scrollbar-class tv-epg-scrollbar-horizontal-hidden h-[226px] xl:h-[255px] 2xl:h-[320px] relative xl:mt-[3px] 2xl:mt-[5px]`}
        >
          <div
            style={{
              width: `${timeWindowMinutes * timeStyleConfig[timeConfig].pixelPerMinute}px`,
              height: `max(${channelSliderHeight}px, 100%)`,
            }}
            className="absolute left-0 top-0 z-20 flex pointer-events-none"
          >
            <div
              style={{
                border: '1.25px solid red',
                boxShadow: '0 0 5px rgba(255, 0, 0, 0.5)',
                transition: 'margin-left 0.5s ease-in-out',
                marginLeft: `${timerLeftMargin}px`,
              }}
              className="h-full z-20 absolute"
            ></div>

            {generateSlots().map((slot, index) => (
              <div
                key={index}
                style={{
                  width: timeStyleConfig[timeConfig].width,
                  borderRight: slot.time.includes('11:30 pm') ? `1px dashed #FFBD29` : 'none',
                }}
                className="h-full flex items-center"
              ></div>
            ))}
          </div>

          {!isEmpty(visibleChannels) && !isEmpty(channelPlaylistMap)
            ? visibleChannels.map((channelItem) => {
                const programs = channelPlaylistMap[channelItem._id] || []

                return (
                  <div
                    key={channelItem._id}
                    style={{
                      width: `${timeWindowMinutes * timeStyleConfig[timeConfig].pixelPerMinute}px`,
                      background: '#11171F',
                    }}
                    className="flex relative mb-[2px] xl:mb-[3px] 2xl:mb-1 h-[36px] xl:h-[40px] 2xl:h-[50px] overflow-hidden rounded"
                  >
                    {!isEmpty(programs)
                      ? programs.map((program, programIndex) => {
                          const startDiffMinutes = Math.floor(
                            (program.startMoment.valueOf() - timeWindowStart.getTime()) / 60000,
                          )
                          const endDiffMinutes = Math.floor(
                            (program.endMoment.valueOf() - timeWindowStart.getTime()) / 60000,
                          )

                          if (endDiffMinutes <= 0 || startDiffMinutes >= timeWindowMinutes) {
                            return null
                          }

                          const leftMinutes = Math.max(0, startDiffMinutes)
                          const visibleDuration = Math.max(
                            1,
                            Math.min(timeWindowMinutes, endDiffMinutes) - leftMinutes,
                          )

                          return (
                            <div
                              key={
                                program.programKey ||
                                `${program.asset_id || program.name}-${programIndex}`
                              }
                              style={{
                                left: `${
                                  leftMinutes * timeStyleConfig[timeConfig].pixelPerMinute
                                }px`,
                                width: `${
                                  visibleDuration * timeStyleConfig[timeConfig].pixelPerMinute
                                }px`,
                                fontSize: timeStyleConfig[timeConfig].fontSize,
                                border: `1px solid ${
                                  getContentTileColor(program, programIndex).borderColor
                                }`,
                                background: getContentTileColor(program, programIndex).bgColor,
                                color: '#FFFFFF',
                                zIndex: 10,
                              }}
                              onMouseEnter={() =>
                                handleMouseEnter(
                                  `${program.asset_id || program.name}-${programIndex}`,
                                )
                              }
                              onMouseLeave={handleMouseLeave}
                              onClick={() =>
                                setSelectedPreviewProgram((currentProgram) =>
                                  currentProgram?.programKey === program.programKey ? {} : program,
                                )
                              }
                              className={`absolute cursor-pointer h-full flex flex-col justify-center items-start px-2 overflow-hidden whitespace-nowrap text-ellipsis rounded xl:rounded-md`}
                            >
                              <div className="w-full">
                                <p className="truncate">{program.title}</p>
                                <p
                                  style={{ color: '#868686' }}
                                  className="text-xs mt-[1px] 2xl:mt-1 truncate"
                                >
                                  {/* {`${program.startMoment.format(
                                    'hh:mm A',
                                  )} - ${program.endMoment.format('hh:mm A')}`} */}
                                  {`${program.startMoment.format('hh:mm A')}`}
                                </p>
                              </div>
                            </div>
                          )
                        })
                      : null}
                  </div>
                )
              })
            : epgLoader
            ? [1, 2, 3, 4, 5, 6, 7].map((_, index) => (
                <div
                  style={{
                    background: '#12171D',
                    borderRadius: '6px 0px 0px 6px',
                  }}
                  className="w-full h-[36px] xl:h-[40px] 2xl:h-[50px] mb-[2px] xl:mb-[3px] 2xl:mb-1 relative animate-pulse"
                  key={index}
                >
                  <div className="flex" style={{ height: '75%', aspectRatio: '1.785' }}>
                    <div className="bg-gray-500 h-full w-full"></div>
                  </div>
                </div>
              ))
            : null}
        </div>

        {!isEmpty(selectedPreviewProgram) ? (
          <ProgramPreview
            selectedPreviewProgram={selectedPreviewProgram}
            setSelectedPreviewProgram={setSelectedPreviewProgram}
          />
        ) : null}
      </div>
    </div>
  )
}

export default IptvPlaylistEPG
